import { AvatarEntity } from '@datachain/ui-sdk/components/avatar';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { castDraft, immerable, produce } from 'immer';
import { OrderedSet, Set } from 'immutable';

import { ElementPermission } from '../../../core/domain/permission-types';
import {
	SubscriptionAction,
	SubscriptionStatus,
} from '../../domain/subscription.types';

interface ISubscriptionItemEntity {
	id: number;
	label: string;
	pictogramUrl: string;
	datablockId: number;
	datablockSource: string;
	sourceUrl: string;
	tags: OrderedSet<AppTagItemEntity>;
	status: SubscriptionStatus;
	actionInProgress: SubscriptionAction;
	dataCount: number;
	updatedBy: AvatarEntity | null;
	permissions: Set<ElementPermission>;
}

const entityDefaults: ISubscriptionItemEntity = {
	id: -1,
	label: '',
	pictogramUrl: '',
	datablockId: -1,
	datablockSource: '',
	sourceUrl: '',
	tags: OrderedSet(),
	status: SubscriptionStatus.Unknown,
	actionInProgress: 'none',
	dataCount: 0,
	updatedBy: null,
	permissions: Set(),
};

export class SubscriptionItemEntity implements ISubscriptionItemEntity {
	private [immerable] = true;
	public id = entityDefaults.id;
	public label = entityDefaults.label;
	public pictogramUrl = entityDefaults.pictogramUrl;
	public datablockId = entityDefaults.datablockId;
	public datablockSource = entityDefaults.datablockSource;
	public sourceUrl = entityDefaults.sourceUrl;
	public tags = entityDefaults.tags;
	public status = entityDefaults.status;
	public actionInProgress = entityDefaults.actionInProgress;
	public dataCount = entityDefaults.dataCount;
	public updatedBy = entityDefaults.updatedBy;
	public permissions = entityDefaults.permissions;

	public static build(
		params: Partial<ISubscriptionItemEntity>
	): SubscriptionItemEntity {
		const entity = new SubscriptionItemEntity();
		entity.id = params.id ?? entityDefaults.id;
		entity.label = params.label ?? entityDefaults.label;
		entity.pictogramUrl = params.pictogramUrl ?? entityDefaults.pictogramUrl;
		entity.datablockId = params.datablockId ?? entityDefaults.datablockId;
		entity.datablockSource =
			params.datablockSource ?? entityDefaults.datablockSource;
		entity.sourceUrl = params.sourceUrl ?? entityDefaults.sourceUrl;
		entity.tags = params.tags ?? entityDefaults.tags;
		entity.status = params.status ?? entityDefaults.status;
		entity.actionInProgress =
			params.actionInProgress ?? entityDefaults.actionInProgress;
		entity.dataCount = params.dataCount ?? entityDefaults.dataCount;
		entity.updatedBy = params.updatedBy ?? entityDefaults.updatedBy;
		entity.permissions = params.permissions ?? entityDefaults.permissions;

		return entity;
	}

	public setActionInProgress(
		action: SubscriptionAction
	): SubscriptionItemEntity {
		return produce(this, (draft) => {
			draft.actionInProgress = castDraft(action);
		});
	}

	public setStatus(status: SubscriptionStatus): SubscriptionItemEntity {
		return produce(this, (draft) => {
			draft.status = castDraft(status);
		});
	}

	public setPermissions(
		permissions: Set<ElementPermission>
	): SubscriptionItemEntity {
		return produce(this, (draft) => {
			draft.permissions = castDraft(permissions);
		});
	}
}
